import React from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { getMyPaletteColor } from "../utils/colorUtils.ts";

interface UploadProgressWidgetProps {
  total: number;
  current: number;
  onClose: () => void;
  bottom?: number;
}

export const UploadProgressWidget: React.FC<UploadProgressWidgetProps> = ({
  total,
  current,
  onClose,
  bottom = 24,
}) => {
  const params = new URLSearchParams(window.location.search);
  const palette = getMyPaletteColor(params.get("color"));
  const { accentColor, isDarkMode } = usePaletteColors(palette);

  const progress = (current / total) * 100;
  const isComplete = current === total;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        bottom: bottom,
        right: 24,
        width: 320,
        borderRadius: 3,
        overflow: "hidden",
        zIndex: 2000,
        bgcolor: isDarkMode ? "#1f2937" : "#ffffff",
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          bgcolor: isComplete ? "success.main" : accentColor,
          p: 1.5,
          display: "flex",
          alignItems: "center",
          color: "#fff",
        }}
      >
        {isComplete ? (
          <CheckIcon sx={{ mr: 1 }} />
        ) : (
          <UploadIcon sx={{ mr: 1 }} />
        )}
        <Typography
          variant="subtitle2"
          sx={{ flexGrow: 1, fontWeight: "bold" }}
        >
          {isComplete
            ? `${total}個のアップロード完了`
            : `${total}個中 ${current}個をアップロード中`}
        </Typography>
        <IconButton size="small" color="inherit" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* プログレスバーエリア */}
      <Box sx={{ p: 2 }}>
        {!isComplete ? (
          <Stack spacing={1}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : "grey.200",
                "& .MuiLinearProgress-bar": { bgcolor: accentColor },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="right"
            >
              {Math.round(progress)}% 完了
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            すべてのファイルを現在のフォルダに保存しました。
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
